import type { ReactNode } from "react";
import { useLocation } from "wouter";
import { ArrowLeft } from "lucide-react";
import { DarkModeToggle } from "@/components/DarkModeToggle";
import { usePageBand } from "@/hooks/use-page-band";

/**
 * Top bar standard CATHO+ paroisses (barre dégradée bleu marine).
 *
 * Extrait du pattern dupliqué sur ~70 pages : bouton retour, séparateur,
 * libellé fil d'Ariane, badge paroisse optionnel, logo / slot droit.
 *
 * Pour les pages avec une top-bar atypique (icône intégrée au breadcrumb,
 * boutons d'action multiples, etc.) : préférer le markup en ligne.
 */
export interface PageHeaderProps {
  /** Cible du bouton retour. Défaut: `/`. */
  backTo?: string;
  /** Libellé du bouton retour. */
  backLabel: ReactNode;
  /** Fil d'Ariane (titre de section). */
  breadcrumb: ReactNode;
  /**
   * Style du fil d'Ariane.
   *  - `strong` (défaut) : texte blanc en `font-medium` — pattern majoritaire
   *  - `muted`           : texte `white/40 text-xs` — pages CMS / finances
   */
  breadcrumbStyle?: "strong" | "muted";
  /** Badge paroisse optionnel (pilule arrondie). */
  parishLabel?: ReactNode;
  /** Slot droit. Défaut : logo CATHO+ blanc. */
  rightSlot?: ReactNode;
  /** Override du `data-testid` du bouton retour. */
  backTestId?: string;
}

const DEFAULT_GRADIENT =
  "linear-gradient(90deg, var(--brand-navy) 0%, var(--brand-navy-light) 50%, var(--brand-royal-light) 100%)";

export function PageHeader({
  backTo = "/",
  backLabel,
  breadcrumb,
  breadcrumbStyle = "strong",
  parishLabel,
  rightSlot,
  backTestId = "button-back-home",
}: PageHeaderProps) {
  const [, setLocation] = useLocation();
  // Signale au shell qu'un bandeau est monté : `AppShellHeader` (toggle
  // dark/light en `fixed top-right`) s'efface pour ne plus recouvrir le logo.
  usePageBand();

  const breadcrumbClass =
    breadcrumbStyle === "muted"
      ? "text-white/40 text-xs"
      : "text-white font-medium text-sm";

  return (
    <header
      className="h-14 flex items-center justify-between pl-16 pr-4 md:px-6 shrink-0"
      style={{ background: DEFAULT_GRADIENT }}
    >
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => setLocation(backTo)}
          className="flex items-center gap-1.5 text-white/60 hover:text-white transition-colors text-sm"
          data-testid={backTestId}
          aria-label={typeof backLabel === "string" ? backLabel : undefined}
        >
          <ArrowLeft className="w-4 h-4"  aria-hidden="true" />
          <span>{backLabel}</span>
        </button>
        <div className="w-px h-5 bg-white/20" aria-hidden="true" />
        <h1 className={breadcrumbClass} aria-level={1}>{breadcrumb}</h1>
        {/* Téléphone : avec la marge laissée au bouton du menu, la pastille
            de la paroisse passait sur quatre lignes et débordait du bandeau ;
            elle s'efface sous `sm` (le nom reste dans le menu). */}
        {parishLabel ? (
          <span className="hidden sm:inline text-xs text-white/50 bg-white/10 rounded-full px-2 py-0.5" data-testid="text-header-paroisse">
            {parishLabel}
          </span>
        ) : null}
      </div>
      {/* Zone droite du bandeau : toggle dark/light intégré À GAUCHE du logo
          CATHO+ (le proto n'a que le logo ici ; le toggle est un acquis à
          préserver — il était auparavant en overlay par-dessus le logo). */}
      <div className="flex items-center gap-2">
        <DarkModeToggle variant="onDark" />
        {rightSlot ?? (
          <img src="/images/logo-white.png" alt="CATHO+" className="h-6" />
        )}
      </div>
    </header>
  );
}

export default PageHeader;
